import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import CostCenterGroupsSettings from "../components/CostCenterGroupsSettings";
import {
  createCostCenterApi,
  listCostCenterGroupsApi,
  DpCostCenterGroup,
  createEmployerApi,
  createLegalParameterApi,
  createPositionApi,
  deleteCostCenterApi,
  deleteLegalParameterApi,
  deletePositionApi,
  listCostCentersApi,
  listEmployersApi,
  listLegalParametersApi,
  listPositionsApi,
  updateCostCenterApi,
  updateEmployerApi,
  updateLegalParameterApi,
  updatePositionApi,
  DpCostCenter,
  DpEmployer,
  DpLegalParameter,
  DpPosition,
} from "../api/settingsApi";
import {
  DpLegalParameterType,
  LEGAL_PARAMETER_TYPE_LABELS,
  LEGAL_PARAMETER_TYPES,
  UF_OPTIONS,
} from "../api/dpTypes";
import {
  formatCnpj,
  formatCurrency,
  formatDateOnly,
  maskCnpjInput,
  orDash,
  toDateInputValue,
} from "../utils/format";
import { DP_PAGE } from "../routes";
import DocumentTypesSettings from "../components/DocumentTypesSettings";
import ProcessTemplatesSettings from "../components/ProcessTemplatesSettings";
import TaxTablesSettings from "../components/TaxTablesSettings";
import BenefitTypesSettings from "../components/BenefitTypesSettings";
import PayrollPolicySettings from "../components/PayrollPolicySettings";
import PayrollItemsSettings from "../components/PayrollItemsSettings";
import HolidaysSettings from "../components/HolidaysSettings";
import StoreSchedulesSettings from "../components/StoreSchedulesSettings";
import KioskDevicesSettings from "../components/KioskDevicesSettings";
import EmployerLogoField from "../components/EmployerLogoField";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

type Tab =
  | "empregadores"
  | "cargos"
  | "centros"
  | "parametros"
  | "beneficios"
  | "tabelas"
  | "politica"
  | "rubricas"
  | "documentos"
  | "checklists"
  | "feriados"
  | "lojas"
  | "quiosques";

const TABS: Tab[] = ["empregadores", "cargos", "centros", "parametros", "beneficios", "tabelas", "politica", "rubricas", "documentos", "checklists", "feriados", "lojas", "quiosques"];

const TAB_LABELS: Record<Tab, string> = {
  empregadores: "Empregadores",
  cargos: "Cargos",
  centros: "Centros de custo",
  parametros: "Parâmetros legais",
  beneficios: "Benefícios",
  tabelas: "Tabelas INSS e IR",
  politica: "Política de pagamento",
  rubricas: "Rubricas",
  documentos: "Tipos de documento",
  checklists: "Checklists",
  feriados: "Feriados",
  lojas: "Lojas e turnos",
  quiosques: "Aparelhos do quiosque",
};

/** As 13 abas em 5 grupos, na navegação lateral (desktop) e no seletor (celular). */
const SETTINGS_GROUPS: { title: string; tabs: Tab[] }[] = [
  { title: "Contratos", tabs: ["empregadores", "cargos", "centros", "parametros"] },
  { title: "Folha", tabs: ["beneficios", "tabelas", "politica", "rubricas"] },
  { title: "Escala da loja", tabs: ["lojas", "feriados"] },
  { title: "Ponto", tabs: ["quiosques"] },
  { title: "Documentos e processos", tabs: ["documentos", "checklists"] },
];

const emptyEmployer = {
  legalName: "",
  tradeName: "",
  cnpj: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  cnoCaepf: "",
  isActive: true,
};

const emptyPosition = { name: "", cbo: "", description: "" };
const emptyCostCenter = { code: "", name: "", storeId: "", isActive: true, groupId: "" };
const emptyParameter = {
  type: "SALARIO_MINIMO" as DpLegalParameterType,
  effectiveFrom: "",
  value: "",
  notes: "",
};

export default function DpSettingsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [searchParams] = useSearchParams();
  // `?aba=` abre direto numa aba (ex.: a tela da folha leva às tabelas de INSS e IR).
  const [tab, setTab] = useState<Tab>(() => {
    const requested = searchParams.get("aba") as Tab;
    return TABS.includes(requested) ? requested : "empregadores";
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [employers, setEmployers] = useState<DpEmployer[]>([]);
  const [positions, setPositions] = useState<DpPosition[]>([]);
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [ccGroups, setCcGroups] = useState<DpCostCenterGroup[]>([]);
  const [stores, setStores] = useState<StorePublic[]>([]);

  const [employerModal, setEmployerModal] = useState<{ id?: string } | null>(null);
  const [employerForm, setEmployerForm] = useState(emptyEmployer);

  const [positionModal, setPositionModal] = useState<{ id?: string } | null>(null);
  const [positionForm, setPositionForm] = useState(emptyPosition);

  const [costCenterModal, setCostCenterModal] = useState<{ id?: string } | null>(null);
  const [costCenterForm, setCostCenterForm] = useState(emptyCostCenter);

  const [parameters, setParameters] = useState<DpLegalParameter[]>([]);
  const [parameterModal, setParameterModal] = useState<{ id?: string } | null>(null);
  const [parameterForm, setParameterForm] = useState(emptyParameter);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [e, p, c, lp, g] = await Promise.all([
        listEmployersApi(token),
        listPositionsApi(token),
        // Inclui inativos: esta é a tela de manutenção, onde reativar precisa ser possível.
        listCostCentersApi(token, true),
        listLegalParametersApi(token),
        listCostCenterGroupsApi(token, true),
      ]);
      setEmployers(e);
      setPositions(p);
      setCostCenters(c);
      setCcGroups(g);
      setParameters(lp);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os cadastros.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    getStoresPaginatedApi(token, { all: true })
      .then((res) => setStores(res.items ?? []))
      .catch(() => setStores([]));
  }, [token]);

  // ── Empregadores ──────────────────────────────────────────────────────────

  const openEmployer = (employer?: DpEmployer) => {
    setEmployerForm(
      employer
        ? {
            legalName: employer.legalName,
            tradeName: employer.tradeName ?? "",
            cnpj: formatCnpj(employer.cnpj),
            address: employer.address ?? "",
            city: employer.city ?? "",
            state: employer.state ?? "",
            zipCode: employer.zipCode ?? "",
            cnoCaepf: employer.cnoCaepf ?? "",
            isActive: employer.isActive,
          }
        : emptyEmployer
    );
    setEmployerModal({ id: employer?.id });
  };

  const saveEmployer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !employerModal) return;
    if (!employerForm.legalName.trim() || !employerForm.cnpj.trim()) {
      toast.error("Informe a razão social e o CNPJ.");
      return;
    }

    const payload = {
      legalName: employerForm.legalName.trim(),
      tradeName: employerForm.tradeName.trim() || undefined,
      cnpj: employerForm.cnpj.replace(/\D/g, ""),
      address: employerForm.address.trim() || undefined,
      city: employerForm.city.trim() || undefined,
      state: employerForm.state || undefined,
      // Vazio limpa (o ponto usa os dois no registro da empresa, Portaria 671).
      zipCode: employerForm.zipCode.replace(/\D/g, ""),
      cnoCaepf: employerForm.cnoCaepf.replace(/\D/g, ""),
      isActive: employerForm.isActive,
    };

    setSaving(true);
    try {
      if (employerModal.id) {
        await updateEmployerApi(token, employerModal.id, payload);
      } else {
        await createEmployerApi(token, payload);
      }
      toast.success("Empregador salvo.");
      setEmployerModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar empregador.");
    } finally {
      setSaving(false);
    }
  };

  // ── Cargos ────────────────────────────────────────────────────────────────

  const openPosition = (position?: DpPosition) => {
    setPositionForm(
      position
        ? {
            name: position.name,
            cbo: position.cbo ?? "",
            description: position.description ?? "",
          }
        : emptyPosition
    );
    setPositionModal({ id: position?.id });
  };

  const savePosition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !positionModal) return;
    if (!positionForm.name.trim()) {
      toast.error("Informe o nome do cargo.");
      return;
    }

    const payload = {
      name: positionForm.name.trim(),
      cbo: positionForm.cbo.trim() || undefined,
      description: positionForm.description.trim() || undefined,
    };

    setSaving(true);
    try {
      if (positionModal.id) {
        await updatePositionApi(token, positionModal.id, payload);
      } else {
        await createPositionApi(token, payload);
      }
      toast.success("Cargo salvo.");
      setPositionModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar cargo.");
    } finally {
      setSaving(false);
    }
  };

  const removePosition = async (id: string) => {
    if (!token) return;
    if (!window.confirm("Excluir este cargo?")) return;
    try {
      await deletePositionApi(token, id);
      toast.success("Cargo excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir cargo.");
    }
  };

  // ── Centros de custo ──────────────────────────────────────────────────────

  const openCostCenter = (costCenter?: DpCostCenter) => {
    setCostCenterForm(
      costCenter
        ? {
            code: costCenter.code,
            name: costCenter.name,
            storeId: costCenter.storeId ?? "",
            isActive: costCenter.isActive,
            groupId: costCenter.groupId ?? "",
          }
        : emptyCostCenter
    );
    setCostCenterModal({ id: costCenter?.id });
  };

  const saveCostCenter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !costCenterModal) return;
    if (!costCenterForm.name.trim() || !costCenterForm.groupId) {
      toast.error("Informe o nome e o grupo.");
      return;
    }

    const payload = {
      name: costCenterForm.name.trim(),
      storeId: costCenterForm.storeId || null,
      isActive: costCenterForm.isActive,
      groupId: costCenterForm.groupId,
    };

    setSaving(true);
    try {
      if (costCenterModal.id) {
        await updateCostCenterApi(token, costCenterModal.id, payload);
      } else {
        await createCostCenterApi(token, payload);
      }
      toast.success("Centro de custo salvo.");
      setCostCenterModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar centro de custo.");
    } finally {
      setSaving(false);
    }
  };

  const removeCostCenter = async (id: string) => {
    if (!token) return;
    if (!window.confirm("Excluir este centro de custo?")) return;
    try {
      await deleteCostCenterApi(token, id);
      toast.success("Centro de custo excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir centro de custo.");
    }
  };

  // ── Parâmetros legais ────────────────────────────────────────────────────

  const openParameter = (parameter?: DpLegalParameter, type?: DpLegalParameterType) => {
    setParameterForm(
      parameter
        ? {
            type: parameter.type,
            effectiveFrom: toDateInputValue(parameter.effectiveFrom),
            value: String(Number(parameter.value)),
            notes: parameter.notes ?? "",
          }
        : { ...emptyParameter, type: type ?? emptyParameter.type }
    );
    setParameterModal({ id: parameter?.id });
  };

  const saveParameter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !parameterModal) return;

    const value = Number(parameterForm.value);
    if (!parameterForm.effectiveFrom || !Number.isFinite(value) || value <= 0) {
      toast.error("Informe a data de vigência e um valor maior que zero.");
      return;
    }

    const payload = {
      type: parameterForm.type,
      effectiveFrom: parameterForm.effectiveFrom,
      value,
      notes: parameterForm.notes.trim() || undefined,
    };

    setSaving(true);
    try {
      if (parameterModal.id) {
        await updateLegalParameterApi(token, parameterModal.id, payload);
      } else {
        await createLegalParameterApi(token, payload);
      }
      toast.success("Parâmetro salvo.");
      setParameterModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar parâmetro.");
    } finally {
      setSaving(false);
    }
  };

  const removeParameter = async (id: string) => {
    if (!token) return;
    if (!window.confirm("Excluir este valor? Os cálculos dessa vigência ficam sem parâmetro.")) return;
    try {
      await deleteLegalParameterApi(token, id);
      toast.success("Parâmetro excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir parâmetro.");
    }
  };

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Cadastros" }]}
            title="Cadastros do DP"
            subtitle="Empregadores, cargos, centros de custo e parâmetros legais usados pelos contratos; tabelas, política e rubricas da folha; lojas, turnos e feriados da escala; tipos de documento e checklists de admissão e desligamento."
            onBack={goBack}
            backLabel="Voltar para o DP"
          />

          <div className="dpeSettings">
            <nav className="dpeSettings__nav" aria-label="Cadastros">
              {SETTINGS_GROUPS.map((group) => (
                <div key={group.title} className="dpeSettings__group">
                  <span className="dpeSettings__groupTitle">{group.title}</span>
                  {group.tabs.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`dpeForm__navLink${tab === t ? " is-active" : ""}`}
                      aria-current={tab === t ? "page" : undefined}
                      onClick={() => setTab(t)}
                    >
                      {TAB_LABELS[t]}
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            <div className="dpeSettings__picker">
              <label htmlFor="dp-settings-tab" className="ecoLabel">Cadastro</label>
              <select
                id="dp-settings-tab"
                className="ecoSelect"
                value={tab}
                onChange={(e) => setTab(e.target.value as Tab)}
              >
                {SETTINGS_GROUPS.map((group) => (
                  <optgroup key={group.title} label={group.title}>
                    {group.tabs.map((t) => (
                      <option key={t} value={t}>{TAB_LABELS[t]}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="dpeSettings__content">

          {loading ? <div className="dpEmpty">Carregando...</div> : null}

          {/* Documentos e checklists (plano B) carregam os próprios dados. */}
          {tab === "documentos" && token ? <DocumentTypesSettings token={token} /> : null}
          {tab === "checklists" && token ? <ProcessTemplatesSettings token={token} /> : null}
          {/* Benefícios personalizáveis (plano odontológico, Gympass…). */}
          {tab === "beneficios" && token ? <BenefitTypesSettings token={token} /> : null}
          {/* Folha para conferência (F1, ADR 0016). */}
          {tab === "tabelas" && token ? <TaxTablesSettings token={token} /> : null}
          {tab === "politica" && token ? <PayrollPolicySettings token={token} /> : null}
          {tab === "rubricas" && token ? <PayrollItemsSettings token={token} /> : null}
          {tab === "feriados" && token ? <HolidaysSettings token={token} /> : null}
          {tab === "lojas" && token ? <StoreSchedulesSettings token={token} /> : null}
          {tab === "quiosques" && token ? <KioskDevicesSettings token={token} /> : null}

          {/* ── Empregadores ──────────────────────────────────────────────── */}
          {!loading && tab === "empregadores" ? (
            <>
              <div className="dpControls">
                <button className="dpBtn dpBtn--primary" onClick={() => openEmployer()}>
                  Novo empregador
                </button>
              </div>

              <div className="dpTableWrapper">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Razão social</th>
                      <th>Nome fantasia</th>
                      <th>CNPJ</th>
                      <th>Cidade/UF</th>
                      <th>Situação</th>
                      <th style={{ width: 100 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="dpEmpty">
                          Nenhum empregador cadastrado. O contrato de trabalho precisa de um CNPJ.
                        </td>
                      </tr>
                    ) : (
                      employers.map((e) => (
                        <tr key={e.id}>
                          <td>{e.legalName}</td>
                          <td>{orDash(e.tradeName)}</td>
                          <td>{formatCnpj(e.cnpj)}</td>
                          <td>
                            {e.city ? `${e.city}${e.state ? `/${e.state}` : ""}` : "—"}
                          </td>
                          <td>
                            <span className={`dpBadge ${e.isActive ? "dpBadge--green" : "dpBadge--gray"}`}>
                              {e.isActive ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td>
                            <button
                              className="dpBtn dpBtn--secondary"
                              style={{ height: 30, padding: "0 10px" }}
                              onClick={() => openEmployer(e)}
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* ── Cargos ────────────────────────────────────────────────────── */}
          {!loading && tab === "cargos" ? (
            <>
              <div className="dpControls">
                <button className="dpBtn dpBtn--primary" onClick={() => openPosition()}>
                  Novo cargo
                </button>
              </div>

              <div className="dpTableWrapper">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Cargo</th>
                      <th>CBO</th>
                      <th>Descrição</th>
                      <th style={{ width: 160 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="dpEmpty">
                          Nenhum cargo cadastrado.
                        </td>
                      </tr>
                    ) : (
                      positions.map((p) => (
                        <tr key={p.id}>
                          <td>{p.name}</td>
                          <td>{orDash(p.cbo)}</td>
                          <td>{orDash(p.description)}</td>
                          <td>
                            <div className="dpTable__actions">
                              <button
                                className="dpBtn dpBtn--secondary"
                                style={{ height: 30, padding: "0 10px" }}
                                onClick={() => openPosition(p)}
                              >
                                Editar
                              </button>
                              <button
                                className="dpBtn dpBtn--danger"
                                style={{ height: 30, padding: "0 10px" }}
                                onClick={() => removePosition(p.id)}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* ── Centros de custo ──────────────────────────────────────────── */}
          {!loading && tab === "centros" ? (
            <>
              <div className="dpControls">
                <button className="dpBtn dpBtn--primary" onClick={() => openCostCenter()}>
                  Novo centro de custo
                </button>
              </div>

              <CostCenterGroupsSettings
                token={token ?? ""}
                groups={ccGroups}
                onChanged={load}
              />

              <p className="dpNotice">
                Cada centro de custo tem uma loja só, ou nenhuma (há centro de custo que não
                corresponde a loja nenhuma, como o administrativo).
              </p>

              <div className="dpTableWrapper">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Nome</th>
                      <th>Grupo</th>
                      <th>Loja</th>
                      <th>Situação</th>
                      <th style={{ width: 160 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costCenters.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="dpEmpty">
                          Nenhum centro de custo cadastrado.
                        </td>
                      </tr>
                    ) : (
                      costCenters.map((c) => (
                        <tr key={c.id}>
                          <td>{c.code}</td>
                          <td>{c.name}</td>
                          <td>{c.group ? `${c.group.code} — ${c.group.name}` : "—"}</td>
                          <td>{orDash(c.store?.nomeFantasia ?? "")}</td>
                          <td>
                            <span className={`dpBadge ${c.isActive ? "dpBadge--green" : "dpBadge--gray"}`}>
                              {c.isActive ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td>
                            <div className="dpTable__actions">
                              <button
                                className="dpBtn dpBtn--secondary"
                                style={{ height: 30, padding: "0 10px" }}
                                onClick={() => openCostCenter(c)}
                              >
                                Editar
                              </button>
                              <button
                                className="dpBtn dpBtn--danger"
                                style={{ height: 30, padding: "0 10px" }}
                                onClick={() => removeCostCenter(c.id)}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* ── Parâmetros legais ─────────────────────────────────────────── */}
          {!loading && tab === "parametros" ? (
            <>
              <p className="dpNotice">
                Cadastre o salário mínimo e o teto/cota do salário-família a cada portaria anual,
                com a data em que passam a valer. Eles alimentam os adicionais sobre o salário
                mínimo e a conferência do salário-família na ficha. Sem eles, a ficha mostra os
                cálculos como pendentes.
              </p>

              {LEGAL_PARAMETER_TYPES.map((type) => {
                const rows = parameters.filter((p) => p.type === type);
                return (
                  <section key={type} className="dpSection">
                    <div className="dpHeader" style={{ marginBottom: 12 }}>
                      <h2 className="dpSection__title" style={{ margin: 0 }}>
                        {LEGAL_PARAMETER_TYPE_LABELS[type]}
                      </h2>
                      <button
                        className="dpBtn dpBtn--primary"
                        onClick={() => openParameter(undefined, type)}
                      >
                        Novo valor
                      </button>
                    </div>

                    <div className="dpTableWrapper">
                      <table className="dpTable">
                        <thead>
                          <tr>
                            <th>Vigência</th>
                            <th>Valor</th>
                            <th>Observação</th>
                            <th style={{ width: 160 }}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="dpEmpty">
                                Nenhum valor cadastrado.
                              </td>
                            </tr>
                          ) : (
                            rows.map((p) => (
                              <tr key={p.id}>
                                <td>
                                  {formatDateOnly(p.effectiveFrom)} —{" "}
                                  {p.effectiveTo ? formatDateOnly(p.effectiveTo) : "vigente"}
                                </td>
                                <td>{formatCurrency(p.value)}</td>
                                <td>{orDash(p.notes)}</td>
                                <td>
                                  <div className="dpTable__actions">
                                    <button
                                      className="dpBtn dpBtn--secondary dpBtn--small"
                                      onClick={() => openParameter(p)}
                                    >
                                      Editar
                                    </button>
                                    <button
                                      className="dpBtn dpBtn--danger dpBtn--small"
                                      onClick={() => removeParameter(p.id)}
                                    >
                                      Excluir
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
            </>
          ) : null}
            </div>
          </div>
        </div>
      </main>

      {/* ── Modal: parâmetro legal ─────────────────────────────────────────── */}
      {parameterModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {parameterModal.id ? "Editar parâmetro legal" : "Novo parâmetro legal"}
              </h2>
              <button className="dpModal__close" onClick={() => setParameterModal(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={saveParameter}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="paramType">Parâmetro *</label>
                  <select
                    id="paramType"
                    className="dpSelect"
                    value={parameterForm.type}
                    onChange={(e) =>
                      setParameterForm((f) => ({ ...f, type: e.target.value as DpLegalParameterType }))
                    }
                    disabled={saving}
                  >
                    {LEGAL_PARAMETER_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {LEGAL_PARAMETER_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="paramFrom">Vigente a partir de *</label>
                  <input
                    id="paramFrom"
                    type="date"
                    className="dpInput"
                    value={parameterForm.effectiveFrom}
                    onChange={(e) => setParameterForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="paramValue">Valor (R$) *</label>
                  <input
                    id="paramValue"
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="dpInput"
                    value={parameterForm.value}
                    onChange={(e) => setParameterForm((f) => ({ ...f, value: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="paramNotes">Observação</label>
                  <input
                    id="paramNotes"
                    className="dpInput"
                    maxLength={500}
                    placeholder="Ex.: a portaria que fixou o valor"
                    value={parameterForm.notes}
                    onChange={(e) => setParameterForm((f) => ({ ...f, notes: e.target.value }))}
                    disabled={saving}
                  />
                </div>
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setParameterModal(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {/* ── Modal: empregador ──────────────────────────────────────────────── */}
      {employerModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {employerModal.id ? "Editar empregador" : "Novo empregador"}
              </h2>
              <button className="dpModal__close" onClick={() => setEmployerModal(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={saveEmployer}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="legalName">Razão social *</label>
                  <input
                    id="legalName"
                    className="dpInput"
                    value={employerForm.legalName}
                    onChange={(e) =>
                      setEmployerForm((f) => ({ ...f, legalName: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="tradeName">Nome fantasia</label>
                  <input
                    id="tradeName"
                    className="dpInput"
                    value={employerForm.tradeName}
                    onChange={(e) =>
                      setEmployerForm((f) => ({ ...f, tradeName: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="cnpj">CNPJ *</label>
                  <input
                    id="cnpj"
                    className="dpInput"
                    value={employerForm.cnpj}
                    placeholder="00.000.000/0000-00"
                    inputMode="numeric"
                    onChange={(e) =>
                      setEmployerForm((f) => ({ ...f, cnpj: maskCnpjInput(e.target.value) }))
                    }
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="address">Endereço</label>
                  <input
                    id="address"
                    className="dpInput"
                    value={employerForm.address}
                    onChange={(e) =>
                      setEmployerForm((f) => ({ ...f, address: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="city">Cidade</label>
                  <input
                    id="city"
                    className="dpInput"
                    value={employerForm.city}
                    onChange={(e) => setEmployerForm((f) => ({ ...f, city: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="state">UF</label>
                  <select
                    id="state"
                    className="dpSelect"
                    value={employerForm.state}
                    onChange={(e) => setEmployerForm((f) => ({ ...f, state: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="">—</option>
                    {UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="zipCode">CEP</label>
                  <input
                    id="zipCode"
                    className="dpInput"
                    inputMode="numeric"
                    maxLength={9}
                    placeholder="00000-000"
                    value={employerForm.zipCode}
                    onChange={(e) => setEmployerForm((f) => ({ ...f, zipCode: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="cnoCaepf">CNO ou CAEPF (se houver)</label>
                  <input
                    id="cnoCaepf"
                    className="dpInput"
                    inputMode="numeric"
                    maxLength={18}
                    value={employerForm.cnoCaepf}
                    onChange={(e) => setEmployerForm((f) => ({ ...f, cnoCaepf: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label>
                    <input
                      type="checkbox"
                      checked={employerForm.isActive}
                      onChange={(e) =>
                        setEmployerForm((f) => ({ ...f, isActive: e.target.checked }))
                      }
                      disabled={saving}
                    />{" "}
                    Ativo
                  </label>
                </div>

                {employerModal.id && token ? (
                  <EmployerLogoField
                    token={token}
                    employerId={employerModal.id}
                    logoAssetId={employers.find((x) => x.id === employerModal.id)?.logoAssetId ?? null}
                    onChanged={(logoAssetId) =>
                      setEmployers((list) => list.map((x) => (x.id === employerModal.id ? { ...x, logoAssetId } : x)))
                    }
                  />
                ) : null}
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setEmployerModal(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {/* ── Modal: cargo ───────────────────────────────────────────────────── */}
      {positionModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {positionModal.id ? "Editar cargo" : "Novo cargo"}
              </h2>
              <button className="dpModal__close" onClick={() => setPositionModal(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={savePosition}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="positionName">Nome do cargo *</label>
                  <input
                    id="positionName"
                    className="dpInput"
                    value={positionForm.name}
                    onChange={(e) => setPositionForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="cbo">CBO</label>
                  <input
                    id="cbo"
                    className="dpInput"
                    value={positionForm.cbo}
                    placeholder="521110"
                    inputMode="numeric"
                    maxLength={6}
                    onChange={(e) =>
                      setPositionForm((f) => ({
                        ...f,
                        cbo: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                    disabled={saving}
                  />
                  <span className="dpFormHint">
                    6 dígitos. Fica gravado em cada versão de contrato como estava na data.
                  </span>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="positionDescription">Descrição</label>
                  <textarea
                    id="positionDescription"
                    className="dpTextarea"
                    value={positionForm.description}
                    maxLength={500}
                    onChange={(e) =>
                      setPositionForm((f) => ({ ...f, description: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setPositionModal(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {/* ── Modal: centro de custo ─────────────────────────────────────────── */}
      {costCenterModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {costCenterModal.id ? "Editar centro de custo" : "Novo centro de custo"}
              </h2>
              <button className="dpModal__close" onClick={() => setCostCenterModal(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={saveCostCenter}>
              <div className="dpModal__body">
                <p className="dpFormHint">
                  {costCenterModal.id ? (
                    <>
                      Código <strong>{costCenterForm.code}</strong> (gerado pelo sistema; muda se o centro
                      trocar de grupo).
                    </>
                  ) : (
                    "O código é gerado ao salvar: o próximo número dentro do grupo (ex.: grupo 2 → 2.1, 2.2…)."
                  )}
                </p>

                <div className="dpFormGroup">
                  <label htmlFor="ccName">Nome *</label>
                  <input
                    id="ccName"
                    className="dpInput"
                    value={costCenterForm.name}
                    maxLength={120}
                    onChange={(e) =>
                      setCostCenterForm((f) => ({ ...f, name: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="ccGroup">Grupo *</label>
                  <select
                    id="ccGroup"
                    className="dpSelect"
                    value={costCenterForm.groupId}
                    onChange={(e) => setCostCenterForm((f) => ({ ...f, groupId: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="">Escolha</option>
                    {ccGroups.map((g) => (
                      <option key={g.id} value={g.id} disabled={!g.isActive && g.id !== costCenterForm.groupId}>
                        {g.code} — {g.name}
                        {g.isActive ? "" : " (inativo)"}
                      </option>
                    ))}
                  </select>
                  <span className="dpFormHint">Onde o centro aparece na DRE do Financeiro.</span>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="ccStore">Loja</label>
                  <select
                    id="ccStore"
                    className="dpSelect"
                    value={costCenterForm.storeId}
                    onChange={(e) => setCostCenterForm((f) => ({ ...f, storeId: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="">Sem loja</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nomeFantasia}
                      </option>
                    ))}
                  </select>
                  <span className="dpFormHint">O centro de custo é atrelado a uma loja só.</span>
                </div>

                <div className="dpFormGroup">
                  <label>
                    <input
                      type="checkbox"
                      checked={costCenterForm.isActive}
                      onChange={(e) =>
                        setCostCenterForm((f) => ({ ...f, isActive: e.target.checked }))
                      }
                      disabled={saving}
                    />{" "}
                    Ativo
                  </label>
                </div>
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setCostCenterModal(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
